import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Building2, Share2, List, Map as MapIcon } from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import MosqueCard from '../MosqueCard';
import RestaurantCard from '../RestaurantCard/RestaurantCard';
import { publicRequest } from '../../requestMethods';
import { FaMosque } from 'react-icons/fa6';
import { Utensils } from 'lucide-react';
import './DistrictPage.scss';
import Footer from '../Footer';
import MosqueLoader from '../../utils/MosqueLoader';
import DishFilter from '../DishFilter/DishFilter';
import { getDishDisplayName } from '../../utils/dishDisplay';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const DISTRICTS = [
    'Alappuzha', 'Ernakulam', 'Idukki', 'Kannur', 'Kasaragod',
    'Kollam', 'Kottayam', 'Kozhikode', 'Malappuram', 'Palakkad',
    'Pathanamthitta', 'Thiruvananthapuram', 'Thrissur', 'Wayanad'
];

// Maximum distance in kilometers for nearby mosque search
const MAX_NEARBY_DISTANCE_KM = 20;

// Custom numbered icon for markers
const createNumberedIcon = (number, isSelected = false, isRestaurant = false) => {
    let color;
    if (isRestaurant) {
        color = isSelected ? '#3B82F6' : '#fcd34d'; // Pastel yellow for restaurants
    } else {
        color = isSelected ? '#3B82F6' : '#1e5631'; // Green for mosques
    }
    return L.divIcon({
        className: 'custom-numbered-marker',
        html: `<div style="background-color: ${color};" class="marker-number">${number}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 30],
        popupAnchor: [0, -30]
    });
};

// Component to update map markers
const MapUpdater = ({ places, selectedPlace, onMarkerClick }) => {
    const map = useMap();
    const markersRef = useRef({});

    useEffect(() => {
        // Clear existing markers
        Object.values(markersRef.current).forEach(marker => {
            if (marker) map.removeLayer(marker);
        });
        markersRef.current = {};

        const bounds = new L.LatLngBounds();
        places.forEach((place, index) => {
            if (place.location?.latitude && place.location?.longitude) {
                const position = [place.location.latitude, place.location.longitude];
                const isSelected = selectedPlace?._id === place._id;
                const isRestaurant = place.type === 'restaurant';
                const icon = createNumberedIcon(index + 1, isSelected, isRestaurant);

                const marker = new L.Marker(position, { icon });
                marker.bindPopup(`
                    <div class="map-popup">
                        <h3>${index + 1}. ${place.name} ${isRestaurant ? '🍽️' : '🕌'}</h3>
                        <p><strong>Type:</strong> ${isRestaurant ? 'Restaurant' : 'Mosque'}</p>
                        <p><strong>District:</strong> ${place.district}</p>
                        <p><strong>Dish:</strong> ${getDishDisplayName(place.dish)}</p>
                        <p><strong>Status:</strong> ${place.status}</p>
                        ${place.description ? `<p>${place.description.substring(0, 100)}...</p>` : ''}
                        ${place.googleMapsLink ? `<a href="${place.googleMapsLink}" target="_blank" rel="noopener noreferrer">View on Google Maps</a>` : ''}
                    </div>
                `);
                marker.on('click', () => {
                    onMarkerClick(place);
                });
                marker.addTo(map);
                markersRef.current[place._id] = marker;
                bounds.extend(position);
            }
        });

        // If a place is selected, zoom to it after markers are created
        if (selectedPlace && selectedPlace.location?.latitude && selectedPlace.location?.longitude) {
            const selectedMarker = markersRef.current[selectedPlace._id];
            if (selectedMarker) {
                // Small delay to ensure marker is fully added
                setTimeout(() => {
                    map.setView([selectedPlace.location.latitude, selectedPlace.location.longitude], 15, {
                        animate: true,
                        duration: 0.5
                    });
                    setTimeout(() => {
                        selectedMarker.openPopup();
                    }, 600);
                }, 50);
            }
        } else if (bounds.isValid()) {
            // Fit bounds to show all places
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [places, map, onMarkerClick, selectedPlace]);

    useEffect(() => {
        if (selectedPlace && selectedPlace.location?.latitude && selectedPlace.location?.longitude) {
            // Wait a bit to ensure markers are created
            const checkMarker = () => {
                const marker = markersRef.current[selectedPlace._id];
                if (marker) {
                    // Update marker icon to show selected state
                    const placeIndex = places.findIndex(p => p._id === selectedPlace._id);
                    if (placeIndex !== -1) {
                        const isRestaurant = selectedPlace.type === 'restaurant';
                        const newIcon = createNumberedIcon(placeIndex + 1, true, isRestaurant);
                        marker.setIcon(newIcon);
                    }
                    
                    // Update all other markers to unselected state
                    places.forEach((p, index) => {
                        if (p._id !== selectedPlace._id) {
                            const otherMarker = markersRef.current[p._id];
                            if (otherMarker) {
                                const isRestaurant = p.type === 'restaurant';
                                const unselectedIcon = createNumberedIcon(index + 1, false, isRestaurant);
                                otherMarker.setIcon(unselectedIcon);
                            }
                        }
                    });
                    
                    // Zoom to selected place
                    map.setView([selectedPlace.location.latitude, selectedPlace.location.longitude], 15, {
                        animate: true,
                        duration: 0.5
                    });
                    
                    // Open popup after zoom animation completes
                    setTimeout(() => {
                        marker.openPopup();
                    }, 600);
                } else {
                    // Marker not ready yet, try again after a short delay
                    setTimeout(checkMarker, 100);
                }
            };
            
            checkMarker();
        } else {
            // Update all markers to unselected state
            places.forEach((place, index) => {
                const marker = markersRef.current[place._id];
                if (marker) {
                    const isRestaurant = place.type === 'restaurant';
                    const newIcon = createNumberedIcon(index + 1, false, isRestaurant);
                    marker.setIcon(newIcon);
                }
            });
        }
    }, [selectedPlace, map, places]);

    return null;
};

// City to district mapping (same as in CitySearch)
const CITY_TO_DISTRICT_MAP = {
    'thrissur': 'thrissur',
    'trichur': 'thrissur',
    'kozhikode': 'kozhikode',
    'calicut': 'kozhikode',
    'ernakulam': 'ernakulam',
    'kochi': 'ernakulam',
    'cochin': 'ernakulam',
    'thiruvananthapuram': 'thiruvananthapuram',
    'trivandrum': 'thiruvananthapuram',
    'kollam': 'kollam',
    'quilon': 'kollam',
    'alappuzha': 'alappuzha',
    'alleppey': 'alappuzha',
    'kottayam': 'kottayam',
    'idukki': 'idukki',
    'palakkad': 'palakkad',
    'palghat': 'palakkad',
    'malappuram': 'malappuram',
    'kannur': 'kannur',
    'cannanore': 'kannur',
    'kasaragod': 'kasaragod',
    'wayanad': 'wayanad',
    'pathanamthitta': 'pathanamthitta'
};

// Calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = (value) => (value * Math.PI) / 180;
    const R = 6371; // Earth's radius in km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

const DistrictPage = () => {
    const [mosques, setMosques] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [places, setPlaces] = useState([]); // Combined mosques and restaurants
    const [filteredPlaces, setFilteredPlaces] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [selectedDish, setSelectedDish] = useState('');
    const [filterNamazFacility, setFilterNamazFacility] = useState(false);
    const [filterWomenFacility, setFilterWomenFacility] = useState(false);
    const [isCitySearch, setIsCitySearch] = useState(false);
    const [mobileView, setMobileView] = useState('list'); // 'list' or 'map'
    const [cityCoords, setCityCoords] = useState(null);
    const navigate = useNavigate();
    const { district, cityName } = useParams();
    const [searchParams] = useSearchParams();
    
    // Get city name and coordinates from query params if available
    const queryCityName = searchParams.get('city');
    const queryLat = searchParams.get('lat');
    const queryLon = searchParams.get('lon');
    
    // Map city name to district if coming from /city/:cityName route
    const actualDistrict = cityName 
        ? (CITY_TO_DISTRICT_MAP[cityName.toLowerCase()] || cityName.toLowerCase())
        : district;
    
    // Use query city name if available
    const finalCityName = queryCityName || cityName;

    useEffect(() => {
        const fetchPlaces = async () => {
            try {
                setLoading(true);
                setError(null);
                setIsCitySearch(false);
                setCityCoords(null);
                
                // Check if this is a city search (not a direct district)
                const isCity = finalCityName && !DISTRICTS.some(d => d.toLowerCase() === actualDistrict.toLowerCase());
                
                let fetchedMosques = [];
                let fetchedRestaurants = [];
                
                if (isCity) {
                    // Use coordinates from query params if available, otherwise geocode
                    let cityLat, cityLon;
                    
                    if (queryLat && queryLon) {
                        // Use coordinates from query params
                        cityLat = parseFloat(queryLat);
                        cityLon = parseFloat(queryLon);
                        setCityCoords({ lat: cityLat, lon: cityLon });
                    } else {
                        // Get city coordinates from Nominatim
                        try {
                            const geocodeResponse = await fetch(
                                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(finalCityName + ', Kerala, India')}&limit=1`,
                                {
                                    headers: {
                                        'User-Agent': 'ChorullaPalli/1.0'
                                    }
                                }
                            );
                            const geocodeData = await geocodeResponse.json();
                            
                            if (geocodeData.length > 0) {
                                cityLat = parseFloat(geocodeData[0].lat);
                                cityLon = parseFloat(geocodeData[0].lon);
                                setCityCoords({ lat: cityLat, lon: cityLon });
                            } else {
                                // City not found, try district search
                                const [mosquesRes, restaurantsRes] = await Promise.all([
                                    publicRequest.get(`/mosques/district/${actualDistrict}`),
                                    publicRequest.get(`/restaurants/district/${actualDistrict}`).catch(() => ({ data: [] }))
                                ]);
                                fetchedMosques = mosquesRes.data;
                                fetchedRestaurants = restaurantsRes.data || [];
                                
                                // Combine and add type field with interleaving
                                const mosquesWithType = fetchedMosques.map(m => ({ ...m, type: 'mosque' }));
                                const restaurantsWithType = fetchedRestaurants.map(r => ({ ...r, type: 'restaurant' }));
                                
                                const interleavePlaces = (mosques, restaurants) => {
                                    const result = [];
                                    let mosqueIndex = 0;
                                    let restaurantIndex = 0;
                                    
                                    if (mosqueIndex < mosques.length) {
                                        result.push(mosques[mosqueIndex++]);
                                    }
                                    if (restaurantIndex < restaurants.length) {
                                        result.push(restaurants[restaurantIndex++]);
                                    }
                                    while (mosqueIndex < mosques.length || restaurantIndex < restaurants.length) {
                                        if (restaurantIndex < restaurants.length) {
                                            result.push(restaurants[restaurantIndex++]);
                                        }
                                        if (mosqueIndex < mosques.length) {
                                            result.push(mosques[mosqueIndex++]);
                                        }
                                    }
                                    return result;
                                };
                                
                                const combined = interleavePlaces(mosquesWithType, restaurantsWithType);
                                setMosques(fetchedMosques);
                                setRestaurants(fetchedRestaurants);
                                setPlaces(combined);
                                return;
                            }
                        } catch (geocodeErr) {
                            console.error('Error geocoding city:', geocodeErr);
                            // Fallback to district search
                            const [mosquesRes, restaurantsRes] = await Promise.all([
                                publicRequest.get(`/mosques/district/${actualDistrict}`),
                                publicRequest.get(`/restaurants/district/${actualDistrict}`).catch(() => ({ data: [] }))
                            ]);
                            fetchedMosques = mosquesRes.data;
                            fetchedRestaurants = restaurantsRes.data || [];
                            
                            const mosquesWithType = fetchedMosques.map(m => ({ ...m, type: 'mosque' }));
                            const restaurantsWithType = fetchedRestaurants.map(r => ({ ...r, type: 'restaurant' }));
                            
                            const interleavePlaces = (mosques, restaurants) => {
                                const result = [];
                                let mosqueIndex = 0;
                                let restaurantIndex = 0;
                                
                                if (mosqueIndex < mosques.length) {
                                    result.push(mosques[mosqueIndex++]);
                                }
                                if (restaurantIndex < restaurants.length) {
                                    result.push(restaurants[restaurantIndex++]);
                                }
                                while (mosqueIndex < mosques.length || restaurantIndex < restaurants.length) {
                                    if (restaurantIndex < restaurants.length) {
                                        result.push(restaurants[restaurantIndex++]);
                                    }
                                    if (mosqueIndex < mosques.length) {
                                        result.push(mosques[mosqueIndex++]);
                                    }
                                }
                                return result;
                            };
                            
                            const combined = interleavePlaces(mosquesWithType, restaurantsWithType);
                            setMosques(fetchedMosques);
                            setRestaurants(fetchedRestaurants);
                            setPlaces(combined);
                            return;
                        }
                    }
                    
                    if (cityLat && cityLon) {
                        // Fetch all mosques and restaurants
                        const [allMosquesRes, allRestaurantsRes] = await Promise.all([
                            publicRequest.get('/mosques/get-all-mosques'),
                            publicRequest.get('/restaurants/get-all-restaurants').catch(() => ({ data: [] }))
                        ]);
                        const allMosques = allMosquesRes.data;
                        const allRestaurants = allRestaurantsRes.data || [];
                        
                        // Calculate distance and filter within MAX_NEARBY_DISTANCE_KM
                        const nearbyMosques = allMosques
                            .filter(m => m.location?.latitude && m.location?.longitude)
                            .map(m => ({
                                ...m,
                                distance: calculateDistance(
                                    cityLat,
                                    cityLon,
                                    m.location.latitude,
                                    m.location.longitude
                                )
                            }))
                            .filter(m => m.distance <= MAX_NEARBY_DISTANCE_KM)
                            .sort((a, b) => a.distance - b.distance);
                        
                        const nearbyRestaurants = allRestaurants
                            .filter(r => r.location?.latitude && r.location?.longitude)
                            .map(r => ({
                                ...r,
                                distance: calculateDistance(
                                    cityLat,
                                    cityLon,
                                    r.location.latitude,
                                    r.location.longitude
                                )
                            }))
                            .filter(r => r.distance <= MAX_NEARBY_DISTANCE_KM)
                            .sort((a, b) => a.distance - b.distance);
                        
                        fetchedMosques = nearbyMosques;
                        fetchedRestaurants = nearbyRestaurants;
                        setIsCitySearch(true);
                    }
                } else {
                    // Regular district search
                    const [mosquesRes, restaurantsRes] = await Promise.all([
                        publicRequest.get(`/mosques/district/${actualDistrict}`),
                        publicRequest.get(`/restaurants/district/${actualDistrict}`).catch(() => ({ data: [] }))
                    ]);
                    fetchedMosques = mosquesRes.data;
                    fetchedRestaurants = restaurantsRes.data || [];
                    
                    // If no places found in district, find nearby within MAX_NEARBY_DISTANCE_KM
                    if (fetchedMosques.length === 0 && fetchedRestaurants.length === 0) {
                        try {
                            // Get district center coordinates
                            const geocodeResponse = await fetch(
                                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(actualDistrict + ', Kerala, India')}&limit=1`,
                                {
                                    headers: {
                                        'User-Agent': 'ChorullaPalli/1.0'
                                    }
                                }
                            );
                            const geocodeData = await geocodeResponse.json();
                            
                            if (geocodeData.length > 0) {
                                const districtLat = parseFloat(geocodeData[0].lat);
                                const districtLon = parseFloat(geocodeData[0].lon);
                                setCityCoords({ lat: districtLat, lon: districtLon });
                                
                                // Fetch all mosques and restaurants and find nearby within MAX_NEARBY_DISTANCE_KM
                                const [allMosquesRes, allRestaurantsRes] = await Promise.all([
                                    publicRequest.get('/mosques/get-all-mosques'),
                                    publicRequest.get('/restaurants/get-all-restaurants').catch(() => ({ data: [] }))
                                ]);
                                const allMosques = allMosquesRes.data;
                                const allRestaurants = allRestaurantsRes.data || [];
                                
                                const nearbyMosques = allMosques
                                    .filter(m => m.location?.latitude && m.location?.longitude)
                                    .map(m => ({
                                        ...m,
                                        distance: calculateDistance(
                                            districtLat,
                                            districtLon,
                                            m.location.latitude,
                                            m.location.longitude
                                        )
                                    }))
                                    .filter(m => m.distance <= MAX_NEARBY_DISTANCE_KM)
                                    .sort((a, b) => a.distance - b.distance);
                                
                                const nearbyRestaurants = allRestaurants
                                    .filter(r => r.location?.latitude && r.location?.longitude)
                                    .map(r => ({
                                        ...r,
                                        distance: calculateDistance(
                                            districtLat,
                                            districtLon,
                                            r.location.latitude,
                                            r.location.longitude
                                        )
                                    }))
                                    .filter(r => r.distance <= MAX_NEARBY_DISTANCE_KM)
                                    .sort((a, b) => a.distance - b.distance);
                                
                                fetchedMosques = nearbyMosques;
                                fetchedRestaurants = nearbyRestaurants;
                                setIsCitySearch(true);
                            }
                        } catch (nearbyErr) {
                            console.error('Error finding nearby places:', nearbyErr);
                        }
                    }
                }
                
                // Combine mosques and restaurants with type field and interleave them
                // Pattern: Mosque (1st), Restaurant (2nd), then alternate Restaurant, Mosque, Restaurant, Mosque...
                const mosquesWithType = fetchedMosques.map(m => ({ ...m, type: 'mosque' }));
                const restaurantsWithType = fetchedRestaurants.map(r => ({ ...r, type: 'restaurant' }));
                
                // Interleave function: prioritize restaurants, start with mosque, then restaurant, then alternate
                const interleavePlaces = (mosques, restaurants) => {
                    const result = [];
                    let mosqueIndex = 0;
                    let restaurantIndex = 0;
                    
                    // First: Mosque (if available)
                    if (mosqueIndex < mosques.length) {
                        result.push(mosques[mosqueIndex++]);
                    }
                    
                    // Second: Restaurant (if available)
                    if (restaurantIndex < restaurants.length) {
                        result.push(restaurants[restaurantIndex++]);
                    }
                    
                    // Then alternate: Restaurant, Mosque, Restaurant, Mosque...
                    while (mosqueIndex < mosques.length || restaurantIndex < restaurants.length) {
                        // Add restaurant first (prioritize restaurants)
                        if (restaurantIndex < restaurants.length) {
                            result.push(restaurants[restaurantIndex++]);
                        }
                        // Then mosque
                        if (mosqueIndex < mosques.length) {
                            result.push(mosques[mosqueIndex++]);
                        }
                    }
                    
                    return result;
                };
                
                const combined = interleavePlaces(mosquesWithType, restaurantsWithType);
                
                setMosques(fetchedMosques);
                setRestaurants(fetchedRestaurants);
                setPlaces(combined);
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to fetch places');
            } finally {
                setLoading(false);
            }
        };

        fetchPlaces();
    }, [actualDistrict, finalCityName, queryLat, queryLon]);

    useEffect(() => {
        let filtered = places;
        if (selectedDish) {
            filtered = filtered.filter(place => getDishDisplayName(place.dish) === selectedDish);
        }
        // Filter by Namaz facility (only for restaurants)
        if (filterNamazFacility) {
            filtered = filtered.filter(place => {
                // Always show mosques (they have prayer facilities by default)
                if (place.type === 'mosque') return true;
                // For restaurants, only show if namazFacility is explicitly true
                if (place.type === 'restaurant') {
                    return Boolean(place.namazFacility) === true;
                }
                return false;
            });
        }
        // Filter by Women facility (only for restaurants)
        if (filterWomenFacility) {
            filtered = filtered.filter(place => {
                // Always show mosques (they have prayer facilities by default)
                if (place.type === 'mosque') return true;
                // For restaurants, only show if womenFacility is explicitly true
                if (place.type === 'restaurant') {
                    return Boolean(place.womenFacility) === true;
                }
                return false;
            });
        }
        setFilteredPlaces(filtered);
    }, [places, selectedDish, filterNamazFacility, filterWomenFacility]);

    const handlePlaceClick = (e, place) => {
        // Don't prevent default - we just want to set selected place to zoom map
        setSelectedPlace(place);
    };

    const handlePlaceDoubleClick = (e, place) => {
        e.stopPropagation();
        e.preventDefault();
        if (place.type === 'restaurant') {
            navigate(`/restaurant/${place.restaurantID}`);
        } else {
            navigate(`/mosque/${place.mosqueID}`);
        }
    };

    const handleShare = async () => {
        const url = window.location.href;
        try {
            if (navigator.share) {
                await navigator.share({
                    title: `Mosques in ${actualDistrict}`,
                    text: `Check out mosques offering Iftar meals in ${actualDistrict}`,
                    url: url
                });
            } else {
                // Fallback: copy to clipboard
                await navigator.clipboard.writeText(url);
                alert('Link copied to clipboard!');
            }
        } catch (err) {
            // User cancelled or error occurred
            console.error('Error sharing:', err);
        }
    };

    // Calculate center for map
    const getMapCenter = () => {
        // If city search, center on city coordinates
        if (isCitySearch && cityCoords) {
            return [cityCoords.lat, cityCoords.lon];
        }
        
        const placesWithCoords = filteredPlaces.filter(p => p.location?.latitude && p.location?.longitude);
        if (placesWithCoords.length === 0) {
            return [10.8505, 76.2711]; // Default: Thrissur, Kerala
        }
        const avgLat = placesWithCoords.reduce((sum, p) => sum + p.location.latitude, 0) / placesWithCoords.length;
        const avgLng = placesWithCoords.reduce((sum, p) => sum + p.location.longitude, 0) / placesWithCoords.length;
        return [avgLat, avgLng];
    };

    if (loading) {
        return <MosqueLoader />;
    }

    if (error) {
        return <div className="district-page__error">{error}</div>;
    }

    return (
        <>
            <div className="district-page">
                <nav className="district-page__navbar">
                    <div className="navbar-content">
                        <Link to="/" className="navbar-logo">
                            <FaMosque className="logo-icon" />
                        </Link>
                    </div>
                </nav>

                <div className="district-page__header">
                    <div className="district-page__header-content">
                        <div className="district-page__header-top">
                            <h1 className="district-page__title">
                                <MapPin className="icon" />
                                {isCitySearch && finalCityName 
                                    ? finalCityName.charAt(0).toUpperCase() + finalCityName.slice(1) + ' (Nearby)'
                                    : actualDistrict.charAt(0).toUpperCase() + actualDistrict.slice(1)}
                            </h1>
                            <button className="district-page__share-btn" onClick={handleShare} title="Share">
                                <Share2 size={20} />
                            </button>
                        </div>
                        <div className="district-page__stats">
                            <div className="stat-card">
                                <FaMosque className="icon" />
                                <div className="stat-info">
                                    <h3>{filteredPlaces.filter(p => p.type === 'mosque').length}</h3>
                                    <p>Mosques</p>
                                </div>
                            </div>
                            <div className="stat-card">
                                <Utensils className="icon" />
                                <div className="stat-info">
                                    <h3>{filteredPlaces.filter(p => p.type === 'restaurant').length}</h3>
                                    <p>Restaurants</p>
                                </div>
                            </div>
                            <div className="stat-card">
                                <Building2 className="icon" />
                                <div className="stat-info">
                                    <h3>{filteredPlaces.length}</h3>
                                    <p>{isCitySearch ? `Total Places (within ${MAX_NEARBY_DISTANCE_KM}km)` : 'Total Places'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="district-page__districts-nav">
                    <div className="districts-wrapper">
                        {DISTRICTS.map((dist) => (
                            <Link
                                key={dist}
                                to={`/${dist.toLowerCase()}`}
                                className={`district-link ${actualDistrict.toLowerCase() === dist.toLowerCase() ? 'active' : ''}`}
                            >
                                {dist}
                            </Link>
                        ))}
                    </div>
                </div>

                <div className="district-page__filters">
                    <div className="filter-container">
                        <label>Filter by Meal Type:</label>
                        <DishFilter
                            selectedDish={selectedDish}
                            onDishChange={setSelectedDish}
                        />
                    </div>
                    <div className="filter-container">
                        <label>Restaurant Facilities:</label>
                        <div className="facility-filters">
                            <label className="facility-filter-checkbox">
                                <input
                                    type="checkbox"
                                    checked={filterNamazFacility}
                                    onChange={(e) => setFilterNamazFacility(e.target.checked)}
                                />
                                <span>🤲 Namaz Facility</span>
                            </label>
                            <label className="facility-filter-checkbox">
                                <input
                                    type="checkbox"
                                    checked={filterWomenFacility}
                                    onChange={(e) => setFilterWomenFacility(e.target.checked)}
                                />
                                <span>🧕 Women Facility</span>
                            </label>
                        </div>
                    </div>
                </div>

                {filteredPlaces.length === 0 ? (
                    <div className="district-page__no-results">
                        <Building2 className="icon" />
                        <p>
                            {isCitySearch 
                                ? `No places found within ${MAX_NEARBY_DISTANCE_KM}km. Try searching for a different location.`
                                : 'No places found matching your criteria'}
                        </p>
                    </div>
                ) : (
                    <div className="district-page__split-view">
                        <div className={`district-page__list-panel ${mobileView === 'list' ? 'active' : ''}`}>
                            <div className="list-header">
                                <h3>{filteredPlaces.length} Places ({filteredPlaces.filter(p => p.type === 'mosque').length} Mosques, {filteredPlaces.filter(p => p.type === 'restaurant').length} Restaurants)</h3>
                            </div>
                            <div className="mosques-list">
                                {filteredPlaces.map((place, index) => (
                                    <div
                                        key={place._id}
                                        className={`mosque-list-item ${place.type === 'restaurant' ? 'restaurant-item' : ''} ${selectedPlace?._id === place._id ? 'selected' : ''}`}
                                        onClickCapture={(e) => {
                                            // Handle in capture phase to prevent card's onClick
                                            const isMapIcon = e.target.closest('.mosque-card__map-icon') || e.target.closest('.restaurant-card__map-icon');
                                            const isCallButton = e.target.closest('.restaurant-card__call-button');
                                            const isDescription = e.target.closest('.mosque-card__description') || e.target.closest('.restaurant-card__description');
                                            
                                            if (!isMapIcon && !isCallButton && !isDescription && (e.target.closest('.mosque-card') || e.target.closest('.restaurant-card'))) {
                                                // Set selected place to zoom map
                                                setSelectedPlace(place);
                                                // Stop event from reaching card's onClick
                                                e.stopPropagation();
                                            }
                                        }}
                                        onClick={(e) => {
                                            // Fallback: handle clicks not on card
                                            const isMapIcon = e.target.closest('.mosque-card__map-icon') || e.target.closest('.restaurant-card__map-icon');
                                            const isCallButton = e.target.closest('.restaurant-card__call-button');
                                            const isDescription = e.target.closest('.mosque-card__description') || e.target.closest('.restaurant-card__description');
                                            const isOnCard = e.target.closest('.mosque-card') || e.target.closest('.restaurant-card');
                                            
                                            if (!isMapIcon && !isCallButton && !isDescription && !isOnCard) {
                                                handlePlaceClick(e, place);
                                            }
                                        }}
                                        onDoubleClick={(e) => {
                                            const isMapIcon = e.target.closest('.mosque-card__map-icon') || e.target.closest('.restaurant-card__map-icon');
                                            const isCallButton = e.target.closest('.restaurant-card__call-button');
                                            const isDescription = e.target.closest('.mosque-card__description') || e.target.closest('.restaurant-card__description');
                                            
                                            if (!isMapIcon && !isCallButton && !isDescription) {
                                                handlePlaceDoubleClick(e, place);
                                            }
                                        }}
                                    >
                                        <div className="mosque-number">{index + 1}</div>
                                        <div style={{ flex: 1, position: 'relative' }}>
                                            <div>
                                                {place.type === 'restaurant' ? (
                                                    <RestaurantCard restaurant={place} />
                                                ) : (
                                                    <MosqueCard mosque={place} />
                                                )}
                                            </div>
                                            {isCitySearch && place.distance && (
                                                <div className="mosque-distance">
                                                    📍 {place.distance.toFixed(1)} km away
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className={`district-page__map-panel ${mobileView === 'map' ? 'active' : ''}`}>
                            <MapContainer
                                center={getMapCenter()}
                                zoom={11}
                                scrollWheelZoom={true}
                                className="leaflet-map"
                            >
                                <TileLayer
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                />
                                <MapUpdater
                                    places={filteredPlaces}
                                    selectedPlace={selectedPlace}
                                    onMarkerClick={(place) => setSelectedPlace(place)}
                                />
                            </MapContainer>
                        </div>
                    </div>
                )}
            </div>
            
            {/* Mobile View Switcher - Fixed Bottom */}
            <div className="district-page__mobile-view-switcher">
                <button
                    className={`mobile-view-btn ${mobileView === 'list' ? 'active' : ''}`}
                    onClick={() => setMobileView('list')}
                >
                    <List size={20} />
                    <span>List</span>
                </button>
                <button
                    className={`mobile-view-btn ${mobileView === 'map' ? 'active' : ''}`}
                    onClick={() => setMobileView('map')}
                >
                    <MapIcon size={20} />
                    <span>Map</span>
                </button>
            </div>
            
            {/* <Footer /> */}
        </>
    );
};

export default DistrictPage;
